import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, User, AuthResponse } from '../services/api';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper to get cookie value
  function getCookie(name: string) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return null;
  }

  // Utility function to safely parse JWT token
  const parseJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      // Could not parse token expiration, skipping refresh check
      return null;
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const token = getCookie('moneycareath') || localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (token && userData && refreshToken) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Check if token is expired and refresh if needed
        try {
          const payload = parseJwt(token);
          if (payload && payload.exp) {
            const tokenExp = payload.exp * 1000;
            if (Date.now() >= tokenExp) {
              refreshTokenFromStorage();
            }
          }
        } catch (tokenError) {
          // Could not parse token expiration, skipping refresh check
        }
      } catch (error) {
        // Error parsing stored user data
        clearStoredAuth();
      }
    }
    setIsLoading(false);
  }, []);

  // Watch for user state changes
  useEffect(() => {
    // User state changed
  }, [user]);

  const clearStoredAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Clear the cookie as well
    document.cookie = 'moneycareath=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
  };

  const refreshTokenFromStorage = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.refreshToken(refreshToken);
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Update the cookie with the new token
      document.cookie = `moneycareath=${response.token}; path=/; max-age=${24 * 60 * 60}; SameSite=Lax`;
      
      setUser(response.user);
    } catch (error) {
      // Token refresh failed
      clearStoredAuth();
      setUser(null);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.login({ Email: email, Password: password });
      
      // Store tokens in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Manually set the cookie from frontend to avoid cross-origin issues
      document.cookie = `moneycareath=${response.token}; path=/; max-age=${24 * 60 * 60}; SameSite=Lax`;
      
      setUser(response.user);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string, confirmPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.register({ 
        Name: name, 
        Email: email, 
        Password: password, 
        ConfirmPassword: confirmPassword 
      });
      
      // Store tokens in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Manually set the cookie from frontend to avoid cross-origin issues
      document.cookie = `moneycareath=${response.token}; path=/; max-age=${24 * 60 * 60}; SameSite=Lax`;
      
      setUser(response.user);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Logout API call failed, but still clear local data
    } finally {
      clearStoredAuth();
      setUser(null);
    }
  };

  const refreshToken = async () => {
    await refreshTokenFromStorage();
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    refreshToken,
    isLoading,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 